/**
 * Questions about the household's finances ("Should I pay off my car loan
 * early?"). Claude gets the aggregated Snapshot, the Profile and the open
 * recommendations up front, and tools that look things up in the Sheet's
 * rows when the summary is not enough. The tools only read data already in
 * the page; nothing is written but the Questions log. See ARCHITECTURE.md, Ask.
 */
import type Anthropic from '@anthropic-ai/sdk'
import { betaZodTool } from '@anthropic-ai/sdk/helpers/beta/zod'
import * as z from 'zod/v4'
import { analyse, ledger, type LedgerRow } from './analysis'
import { catalog } from './categories'
import { tiller } from './tiller'
import { withoutUnfinished, mergeSplitAccounts, oneHoldingsSource } from './duplicates'
import { formatDate, formatTimestamp } from './dates'
import { tokens, type Meter } from './usage'
import type { Database, Row } from './google/sheets'

export const ASK_MODEL = 'claude-opus-5-5'

/** Rows a tool returns at most, so one lookup can't flood the context. */
const ROW_LIMIT = 60

export interface Tables {
  accounts: Row[]
  balances: Row[]
  transactions: Row[]
  holdings: Row[]
  documents: Row[]
  profile: Row[]
  recommendations: Row[]
  manual: Row[]
  corrections?: Row[]
  rules?: Row[]
  categories?: Row[]
}

export async function readTables(db: Database): Promise<Tables> {
  // One request: Sheets allows 60 reads a minute per person.
  const t = await db.readMany(['Accounts', 'Balances', 'Transactions', 'Holdings', 'Documents', 'Profile', 'Recommendations', 'Manual', 'Corrections', 'Rules', 'Categories'] as const)
  const { Accounts: accounts, Balances: balances, Transactions: transactions, Holdings: holdings, Documents: documents, Profile: profile, Recommendations: recommendations, Manual: manual, Corrections: corrections, Rules: rules, Categories: categories } = t
  // A linked Tiller sheet adds rows in memory only; see tiller.ts.
  return tiller.merge(
    withoutUnfinished(
      mergeSplitAccounts({
        accounts: accounts.rows,
        balances: balances.rows,
        transactions: transactions.rows,
        holdings: oneHoldingsSource(holdings.rows),
        documents: documents.rows,
        profile: profile.rows,
        recommendations: recommendations.rows,
        manual: manual.rows,
        corrections: corrections.rows,
        rules: rules.rows,
        categories: categories.rows,
      }),
    ),
  )
}

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))
const round = (n: number) => Math.round(n * 100) / 100
const pick = (r: Row, keys: string[]) => Object.fromEntries(keys.map((k) => [k, r[k] ?? null]))

const dateRange = {
  from: z.string().describe('YYYY-MM-DD inclusive, or "" for no lower bound'),
  to: z.string().describe('YYYY-MM-DD inclusive, or "" for no upper bound'),
}
const inRange = (date: string, from: string, to: string) => (!from || date >= from) && (!to || date <= to)

/** What a tool call is doing, for the progress line under the question. */
export type ToolNote = (note: string) => void

export function tools(t: Tables, note: ToolNote) {
  // The lookups see the rows the dashboard counts: de-duplicated, with the
  // person's corrections, and with how each one is counted.
  const rows = ledger(t)
  const cats = catalog(t.categories)
  // A category is named by its full name ("Cars / Fuel"), its parent ("Cars", for all of them) or its id.
  const named = cats.list.map((c) => cats.label(c.id))
  const inCategory = (q: string, r: LedgerRow) => {
    const x = q.trim().toLowerCase()
    return !x || r.category === q.trim() || r.group.toLowerCase() === x || cats.label(r.category).toLowerCase() === x
  }
  const categoryHelp = `The household's categories: ${named.join('; ')}. Give a parent such as "${cats.list[0]?.category ?? 'Dining'}" for all of its subcategories.`
  return [
    betaZodTool({
      name: 'search_transactions',
      description:
        'Find transactions by text (matched against merchant and description, case-insensitive), category, account and date range. Returns the matching rows (newest first, at most 60) and the count and sum of all matches.',
      eager_input_streaming: true,
      inputSchema: z.object({
        text: z.string().describe('Words to match, or "" for any'),
        category: z.string().describe(`One category, or "" for any. ${categoryHelp}`),
        account_id: z.string().describe('One account_id, or "" for all'),
        ...dateRange,
        direction: z.enum(['out', 'in', 'any']).describe('out = money leaving (negative amounts), in = money arriving'),
      }),
      run: async (q) => {
        note(`Searching transactions${q.text ? ` for “${q.text}”` : ''}${q.category ? ` in ${q.category}` : ''}`)
        const needle = q.text.toLowerCase()
        const hits = rows
          .filter((r) => {
            const amount = Number(r.amount)
            return (
              (!needle || `${str(r.merchant)} ${str(r.description)}`.toLowerCase().includes(needle)) &&
              inCategory(q.category, r) &&
              (!q.account_id || str(r.account_id) === q.account_id) &&
              inRange(str(r.date), q.from, q.to) &&
              (q.direction === 'any' || (q.direction === 'out' ? amount < 0 : amount > 0))
            )
          })
          .sort((a, b) => str(b.date).localeCompare(str(a.date)))
        const sum = hits.reduce((s, r) => s + (Number(r.amount) || 0), 0)
        return JSON.stringify({
          count: hits.length,
          sum: round(sum),
          rows: hits.slice(0, ROW_LIMIT).map((r) => ({
            date: r.date,
            account_id: r.account_id,
            merchant: r.merchant,
            description: r.description,
            amount: r.amount,
            category: cats.label(r.category),
            counted_as: r.treatment,
          })),
          truncated: hits.length > ROW_LIMIT,
        })
      },
    }),
    betaZodTool({
      name: 'spending_summary',
      description:
        "Totals of spending exactly as the dashboard counts it (card and bank accounts; not transfers between own accounts, income, investment-account activity or purchases marked one-off) grouped by month, category or merchant, over a date range and optionally one category.",
      eager_input_streaming: true,
      inputSchema: z.object({
        group_by: z.enum(['month', 'category', 'merchant']),
        category: z.string().describe(`Limit to one category, or "" for all. ${categoryHelp} Grouping by category gives each full name.`),
        ...dateRange,
      }),
      run: async (q) => {
        note(`Totalling spending by ${q.group_by}`)
        const totals = new Map<string, { total: number; count: number }>()
        for (const r of rows) {
          if (r.treatment !== 'spending') continue
          if (!inCategory(q.category, r)) continue
          if (!inRange(r.date, q.from, q.to)) continue
          const key = q.group_by === 'month' ? r.month : q.group_by === 'category' ? cats.label(r.category) : r.merchant
          const g = totals.get(key) ?? { total: 0, count: 0 }
          g.total += -r.amount
          g.count += 1
          totals.set(key, g)
        }
        const out = [...totals].map(([key, g]) => ({ [q.group_by]: key, total: round(g.total), count: g.count }))
        out.sort((a, b) => (q.group_by === 'month' ? String(a.month).localeCompare(String(b.month)) : b.total - a.total))
        return JSON.stringify(out.slice(0, ROW_LIMIT))
      },
    }),
    betaZodTool({
      name: 'account_history',
      description: "One account's details, every balance recorded for it (with rate and credit limit), and its holdings on each statement date.",
      eager_input_streaming: true,
      inputSchema: z.object({ account_id: z.string() }),
      run: async ({ account_id }) => {
        const account = t.accounts.find((a) => str(a.account_id) === account_id)
        note(`Reading ${account ? `${str(account.institution)} ${str(account.name)}` : account_id}`)
        if (!account) return JSON.stringify({ error: `No account ${account_id}. Known ids: ${t.accounts.map((a) => str(a.account_id)).join(', ')}` })
        return JSON.stringify({
          account: pick(account, ['account_id', 'institution', 'kind', 'name', 'last4', 'opened', 'closed', 'notes']),
          balances: t.balances
            .filter((b) => str(b.account_id) === account_id)
            .sort((a, b) => str(a.as_of).localeCompare(str(b.as_of)))
            .map((b) => pick(b, ['as_of', 'balance', 'credit_limit', 'apr'])),
          holdings: t.holdings
            .filter((h) => str(h.account_id) === account_id)
            .slice(-ROW_LIMIT)
            .map((h) => pick(h, ['as_of', 'symbol', 'name', 'quantity', 'price', 'value', 'cost_basis', 'asset_class', 'expense_ratio'])),
        })
      },
    }),
    betaZodTool({
      name: 'find_holdings',
      description: 'Every holding of a symbol (or name containing the text) across all accounts, on each statement date.',
      eager_input_streaming: true,
      inputSchema: z.object({ text: z.string().describe('Ticker such as VTI, or part of a fund name') }),
      run: async ({ text }) => {
        note(`Looking up holdings of ${text}`)
        const needle = text.toLowerCase()
        const rows = t.holdings.filter((h) => str(h.symbol).toLowerCase() === needle || str(h.name).toLowerCase().includes(needle))
        return JSON.stringify(rows.slice(0, ROW_LIMIT).map((h) => pick(h, ['account_id', 'as_of', 'symbol', 'name', 'quantity', 'value', 'cost_basis', 'asset_class', 'expense_ratio'])))
      },
    }),
    betaZodTool({
      name: 'list_documents',
      description:
        'Every document that has been read: type, institution, accounts, period, status and Claude\'s one-line summary. Use it to say which statements exist and which are missing.',
      eager_input_streaming: true,
      inputSchema: z.object({}),
      run: async () => {
        note('Checking which documents are on file')
        return JSON.stringify(t.documents.map((d) => pick(d, ['name', 'doc_type', 'institution', 'account_id', 'period_start', 'period_end', 'status', 'summary'])))
      },
    }),
  ]
}

const SYSTEM = `You answer one household's questions about their own finances, as a senior financial planner would: direct, specific, quantified, no generic filler.

You have, below, an aggregated snapshot of their finances computed from the statements they uploaded, their profile facts, and the open recommendations from their last review. Use the tools to look up anything the summary does not answer — individual transactions, an account's full history, holdings of a fund, which documents exist — rather than guessing.

How to answer:
- Lead with the answer in one or two sentences, then the reasoning with their own figures, then what would change the answer.
- Every figure you cite comes from the data or a tool result; say which account or date it belongs to. Every date is YYYY-MM-DD.
- Market rates and product terms come from your general knowledge: give them as approximate and say "verify current rates".
- If the data needed is not on file (for example a loan statement for a loan question), say exactly which document to drop into the Finance Buddy folder, and still give the general rule and the numbers you can.
- Profile rows with source "user" are facts set by the person. Accounts with "manual": true were entered by hand and are the person's estimates.
- Where the decision needs a CPA, attorney or insurance broker, say so.
- Format: short paragraphs, "- " bullets where a list helps, **bold** sparingly, "### " for a heading only in long answers. No tables.`

export interface Turn {
  question: string
  answer: string
}

/** Streams an answer; `onText` receives each text delta, `onNote` each lookup. */
export async function ask(
  anthropic: Anthropic,
  t: Tables,
  question: string,
  history: Turn[],
  onText: (delta: string) => void,
  onNote: ToolNote,
  meter?: Meter,
): Promise<string> {
  const started = performance.now()
  const usages: Anthropic.Beta.BetaUsage[] = []
  let model: string = ASK_MODEL
  const snapshot = analyse(t)
  const context = {
    today: formatDate(new Date()),
    snapshot,
    account_ids: t.accounts.map((a) => pick(a, ['account_id', 'institution', 'kind', 'name'])),
    profile: t.profile.map((r) => ({ ...pick(r, ['key', 'value', 'basis']), source: String(r.source ?? '').trim().toLowerCase() === 'inferred' ? 'inferred' : 'user' })),
    open_recommendations: t.recommendations.filter((r) => r.status === 'new').map((r) => pick(r, ['priority', 'area', 'title', 'next_step'])),
  }

  const runner = anthropic.beta.messages.toolRunner({
    model: ASK_MODEL,
    max_tokens: 16000,
    stream: true,
    max_iterations: 8,
    thinking: { type: 'adaptive' },
    output_config: { effort: 'medium' },
    system: [
      { type: 'text', text: SYSTEM },
      // The context is identical for every follow-up, so cache it.
      { type: 'text', text: `Household data as JSON:\n${JSON.stringify(context)}`, cache_control: { type: 'ephemeral' } },
    ],
    tools: tools(t, onNote),
    messages: [
      ...history.flatMap((h) => [
        { role: 'user' as const, content: h.question },
        { role: 'assistant' as const, content: h.answer },
      ]),
      { role: 'user', content: question },
    ],
  })

  let answer = ''
  // Recorded even when a round fails: the rounds before it were paid for.
  const record = () =>
    usages.length && meter?.({ kind: 'ask', ref: question, model, seconds: (performance.now() - started) / 1000, ...tokens(...usages) })
  try {
    for await (const stream of runner) {
      // A new step after a lookup: separate its text from what came before.
      if (answer && !answer.endsWith('\n')) {
        answer += '\n\n'
        onText('\n\n')
      }
      stream.on('text', (delta) => {
        answer += delta
        onText(delta)
      })
      const message = await stream.finalMessage()
      usages.push(message.usage)
      model = message.model
      if (message.stop_reason === 'refusal') throw new Error('Claude declined to answer this question')
      if (message.stop_reason === 'max_tokens') throw new Error('The answer ran past its length limit')
    }
  } finally {
    // Every lookup round is its own API call; the question's cost is their sum.
    record()
  }
  return answer.trim()
}

/** Appended, so two devices asking at once both keep their question. */
export async function saveQuestion(db: Database, turn: Turn): Promise<void> {
  await db.append('Questions', [{ asked_at: formatTimestamp(new Date()), question: turn.question, answer: turn.answer, model: ASK_MODEL }])
}

export async function pastQuestions(db: Database): Promise<(Turn & { asked_at: string })[]> {
  const table = await db.read('Questions')
  return table.rows.map((r) => ({ asked_at: str(r.asked_at), question: str(r.question), answer: str(r.answer) })).reverse()
}
